import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Job } from '../generated/gql-types';
import { TimesheetSubmissionFrequency } from '../coreLoopConfiguration/coreLoopConfiguration.types';
import { isJobScheduledOnCalendarDay } from '../job/workingDays/workingDays.util';
import {
  getJobDisplayScheduleEndDay,
  getJobDisplayScheduleStartDay,
} from './jobScheduleBounds.util';

dayjs.extend(advancedFormat);
dayjs.extend(isoWeek);

export const PERIOD_TIMESHEET_SAVE_EXPLANATION =
  'Save stores this day on this phone only. Your client cannot see it, and you can still change it.';

export const PERIOD_TIMESHEET_SUBMIT_EXPLANATION =
  'Submit sends this whole period to your client once. After that you cannot edit these days.';

export const PERIOD_TIMESHEET_SUBMIT_WAITING_EXPLANATION =
  'Submit stays off until the last work day of this period, and until every work day is saved or marked as did not work.';

export type PeriodBlock = {
  id: string;
  start: Dayjs;
  end: Dayjs;
};

export type PeriodDayDraft = {
  dateISO: string;
  didNotWork: boolean;
  clockInTimestamp?: string;
  clockOutTimestamp?: string;
  breakMinutes?: number;
};

export type PeriodTimesheetDraft = {
  jobId: string;
  blockId: string;
  days: PeriodDayDraft[];
};

const startOfMonday = (day: Dayjs): Dayjs => day.startOf('day').isoWeekday(1);

/** Monday–Sunday week, a pair of those weeks, or a calendar month. */
export const getPeriodBlock = (
  frequency: TimesheetSubmissionFrequency,
  anchor: Dayjs,
): PeriodBlock | null => {
  const day = anchor.startOf('day');

  switch (frequency) {
    case TimesheetSubmissionFrequency.Day:
      return null;
    case TimesheetSubmissionFrequency.Week: {
      const start = startOfMonday(day);
      return {
        id: `${start.isoWeekYear()}-W${String(start.isoWeek()).padStart(2, '0')}`,
        start,
        end: start.add(6, 'day'),
      };
    }
    case TimesheetSubmissionFrequency.Fortnight: {
      const weekStart = startOfMonday(day);
      const weekNumber = weekStart.isoWeek();
      const fortnightStart =
        weekNumber % 2 === 1 ? weekStart : weekStart.subtract(7, 'day');
      const fortnightEnd = fortnightStart.add(13, 'day');
      const endWeek = fortnightStart.add(7, 'day').isoWeek();
      return {
        id: `${fortnightStart.isoWeekYear()}-W${String(fortnightStart.isoWeek()).padStart(2, '0')}-W${String(endWeek).padStart(2, '0')}`,
        start: fortnightStart,
        end: fortnightEnd,
      };
    }
    case TimesheetSubmissionFrequency.Month: {
      const start = day.startOf('month');
      return {
        id: start.format('YYYY-MM'),
        start,
        end: day.endOf('month').startOf('day'),
      };
    }
    default:
      return null;
  }
};

export type PayRunPeriod = {
  startDate: string; // YYYY-MM-DD, inclusive
  endDate: string; // YYYY-MM-DD, inclusive
};

const formatPayRunDay = (day: Dayjs): string => day.format('YYYY-MM-DD');

const getFixedLengthPayRun = (
  anchor: Dayjs,
  asOfDay: Dayjs,
  lengthInDays: number,
): PayRunPeriod => {
  const daysSinceAnchor = asOfDay.diff(anchor, 'day');
  const periodIndex = Math.floor(daysSinceAnchor / lengthInDays);
  const start = dayjs(anchor).add(periodIndex * lengthInDays, 'day');
  const end = dayjs(start).add(lengthInDays - 1, 'day');
  return {
    startDate: formatPayRunDay(start),
    endDate: formatPayRunDay(end),
  };
};

/**
 * Current pay-run window containing `asOf`.
 * DAY is that calendar day. WEEK and FORTNIGHT step by 7 and 14 days from
 * `payRunCycleAnchorDate`. MONTH is the calendar month of `asOf`.
 */
export const getCurrentPayrunCycleDate = (
  payRunCycleAnchorDate: string,
  frequency: TimesheetSubmissionFrequency,
  asOf: string,
): PayRunPeriod => {
  const anchor = dayjs(payRunCycleAnchorDate).startOf('day');
  const asOfDay = dayjs(asOf).startOf('day');

  switch (frequency) {
    case TimesheetSubmissionFrequency.Day:
      return {
        startDate: formatPayRunDay(asOfDay),
        endDate: formatPayRunDay(asOfDay),
      };
    case TimesheetSubmissionFrequency.Week:
      return getFixedLengthPayRun(anchor, asOfDay, 7);
    case TimesheetSubmissionFrequency.Fortnight:
      return getFixedLengthPayRun(anchor, asOfDay, 14);
    case TimesheetSubmissionFrequency.Month:
      return {
        startDate: formatPayRunDay(dayjs(asOfDay).startOf('month')),
        endDate: formatPayRunDay(dayjs(asOfDay).endOf('month')),
      };
    default:
      return {
        startDate: formatPayRunDay(asOfDay),
        endDate: formatPayRunDay(asOfDay),
      };
  }
};

/** Pay-run window immediately before the one containing `asOf`. */
export const getLastPayrunCycleDate = (
  payRunCycleAnchorDate: string,
  frequency: TimesheetSubmissionFrequency,
  asOf: string,
): PayRunPeriod => {
  const current = getCurrentPayrunCycleDate(
    payRunCycleAnchorDate,
    frequency,
    asOf,
  );
  const dayBeforeCurrent = dayjs(current.startDate)
    .subtract(1, 'day')
    .format('YYYY-MM-DD');
  return getCurrentPayrunCycleDate(
    payRunCycleAnchorDate,
    frequency,
    dayBeforeCurrent,
  );
};

/** Start of the previous pay run through the end of the current one. */
export const getOpenPayRunDateBounds = (
  payRunCycleAnchorDate: string,
  frequency: TimesheetSubmissionFrequency,
  asOf: string,
): { startDate: string; endDate: string } => {
  const last = getLastPayrunCycleDate(payRunCycleAnchorDate, frequency, asOf);
  const current = getCurrentPayrunCycleDate(
    payRunCycleAnchorDate,
    frequency,
    asOf,
  );
  return {
    startDate: last.startDate,
    endDate: current.endDate,
  };
};

export const isDateInOpenPayRun = (
  dateISO: string,
  bounds: { startDate: string; endDate: string },
): boolean => dateISO >= bounds.startDate && dateISO <= bounds.endDate;

/** e.g. 28th, Sep - 4th, Oct */
export const formatPayRunPeriodLabel = (period: PayRunPeriod): string => {
  const start = dayjs(period.startDate, 'YYYY-MM-DD');
  const end = dayjs(period.endDate, 'YYYY-MM-DD');
  return `${start.format('Do, MMM')} - ${end.format('Do, MMM')}`;
};

/** Dates grouped into the pay run that contains them, oldest pay run first. */
export const groupDateISOsByPayRun = (
  dateISOs: string[],
  payRunCycleAnchorDate: string,
  frequency: TimesheetSubmissionFrequency,
): { period: PayRunPeriod; dateISOs: string[] }[] => {
  const groups = new Map<
    string,
    { period: PayRunPeriod; dateISOs: string[] }
  >();
  dateISOs.forEach((dateISO) => {
    const period = getCurrentPayrunCycleDate(
      payRunCycleAnchorDate,
      frequency,
      dateISO,
    );
    const key = `${period.startDate}_${period.endDate}`;
    const group = groups.get(key) ?? { period, dateISOs: [] };
    group.dateISOs.push(dateISO);
    groups.set(key, group);
  });
  return [...groups.values()]
    .map((group) => ({
      period: group.period,
      dateISOs: [...group.dateISOs].sort(),
    }))
    .sort((left, right) =>
      left.period.startDate.localeCompare(right.period.startDate),
    );
};

/** Every rostered working day from the job start through the job end. */
export const listJobRosteredDateISOs = (job: Job): string[] => {
  const scheduleStart = getJobDisplayScheduleStartDay(job);
  const scheduleEnd = getJobDisplayScheduleEndDay(job);
  if (
    !scheduleStart ||
    !scheduleEnd ||
    scheduleStart.isAfter(scheduleEnd, 'day')
  ) {
    return [];
  }

  const dates: string[] = [];
  let cursor = scheduleStart.startOf('day');
  while (!cursor.isAfter(scheduleEnd, 'day')) {
    if (isJobScheduledOnCalendarDay(job, cursor, scheduleStart, scheduleEnd)) {
      dates.push(cursor.format('YYYY-MM-DD'));
    }
    cursor = cursor.add(1, 'day');
  }
  return dates;
};

export const listRosteredDateISOs = (
  job: Job,
  block: PeriodBlock,
): string[] => {
  const scheduleStart = getJobDisplayScheduleStartDay(job);
  const scheduleEnd = getJobDisplayScheduleEndDay(job);
  if (!scheduleStart || !scheduleEnd) {
    return [];
  }

  const first = block.start.isAfter(scheduleStart, 'day')
    ? block.start
    : scheduleStart;
  const last = block.end.isBefore(scheduleEnd, 'day') ? block.end : scheduleEnd;
  if (first.isAfter(last, 'day')) {
    return [];
  }

  const dates: string[] = [];
  let cursor = first.startOf('day');
  while (!cursor.isAfter(last, 'day')) {
    if (isJobScheduledOnCalendarDay(job, cursor, scheduleStart, scheduleEnd)) {
      dates.push(cursor.format('YYYY-MM-DD'));
    }
    cursor = cursor.add(1, 'day');
  }
  return dates;
};

export const isPeriodDayFilled = (
  draft: PeriodDayDraft | undefined,
): boolean => {
  if (!draft) {
    return false;
  }
  if (draft.didNotWork) {
    return true;
  }
  return (
    !!draft.clockInTimestamp &&
    !!draft.clockOutTimestamp &&
    typeof draft.breakMinutes === 'number'
  );
};

export const canAddExtraPeriodDay = (params: {
  job: Job;
  dateISO: string;
  today: Dayjs;
  includedDateISOs: string[];
  /** Last pay run start through current pay run end. */
  payRunBounds: { startDate: string; endDate: string };
}): boolean => {
  const scheduleStart = getJobDisplayScheduleStartDay(params.job);
  const scheduleEnd = getJobDisplayScheduleEndDay(params.job);
  if (!scheduleStart || !scheduleEnd) {
    return false;
  }

  const day = dayjs(params.dateISO, 'YYYY-MM-DD').startOf('day');
  if (!day.isValid()) {
    return false;
  }
  if (day.isBefore(scheduleStart, 'day') || day.isAfter(scheduleEnd, 'day')) {
    return false;
  }
  if (!isDateInOpenPayRun(params.dateISO, params.payRunBounds)) {
    return false;
  }
  if (day.isAfter(params.today, 'day')) {
    return false;
  }
  if (params.includedDateISOs.includes(params.dateISO)) {
    return false;
  }
  if (
    isJobScheduledOnCalendarDay(params.job, day, scheduleStart, scheduleEnd)
  ) {
    return false;
  }
  return true;
};

const draftByDate = (
  drafts: PeriodDayDraft[],
): Record<string, PeriodDayDraft> =>
  drafts.reduce(
    (acc, draft) => {
      acc[draft.dateISO] = draft;
      return acc;
    },
    {} as Record<string, PeriodDayDraft>,
  );

/**
 * Submit sends outstanding days on or before today. Later rostered days do not block it.
 */
export const getPeriodSubmitState = (params: {
  rosteredDateISOs: string[];
  drafts: PeriodDayDraft[];
  today: Dayjs;
  payRunCycleAnchorDate: string;
  frequency: TimesheetSubmissionFrequency;
}): { canSubmit: boolean; reason: string | null } => {
  const byDate = draftByDate(params.drafts);
  const todayISO = params.today.format('YYYY-MM-DD');
  const dueRosteredDateISOs = params.rosteredDateISOs.filter(
    (dateISO) => dateISO <= todayISO,
  );
  const dueAddedDays = params.drafts.filter(
    (draft) =>
      !params.rosteredDateISOs.includes(draft.dateISO) &&
      draft.dateISO <= todayISO,
  );
  if (dueRosteredDateISOs.length === 0 && dueAddedDays.length === 0) {
    return {
      canSubmit: false,
      reason: 'There are no work days to submit.',
    };
  }

  const missingDateISOs = [
    ...dueRosteredDateISOs.filter(
      (dateISO) => !isPeriodDayFilled(byDate[dateISO]),
    ),
    ...dueAddedDays
      .filter((draft) => !isPeriodDayFilled(draft))
      .map((draft) => draft.dateISO),
  ];
  if (missingDateISOs.length > 0) {
    const labels = groupDateISOsByPayRun(
      missingDateISOs,
      params.payRunCycleAnchorDate,
      params.frequency,
    )
      .map((group) => formatPayRunPeriodLabel(group.period))
      .join('\n');
    return {
      canSubmit: false,
      reason: `Please add your hours and breaks or mark "did not work" for the period below.\n${labels}.`,
    };
  }

  return { canSubmit: true, reason: null };
};

export const periodDraftStorageKey = (userId: string, jobId: string): string =>
  `period-timesheet:${userId}:${jobId}`;

export const workedDraftsForSubmit = (
  drafts: PeriodDayDraft[],
): PeriodDayDraft[] =>
  drafts.filter(
    (draft) =>
      !draft.didNotWork &&
      !!draft.clockInTimestamp &&
      !!draft.clockOutTimestamp &&
      typeof draft.breakMinutes === 'number',
  );

export const notWorkedDraftsForSubmit = (
  drafts: PeriodDayDraft[],
): PeriodDayDraft[] => drafts.filter((draft) => draft.didNotWork);
